import React, { FunctionComponent, useState, useEffect, useCallback, MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, MenuItem, makeStyles, TextField, Snackbar, Button, Menu } from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import classNames from 'classnames';
import { uniq } from 'lodash';
import Header from '../../components/PageHeader/Header';
import { Skeleton } from '@material-ui/lab';
import Title from '../../components/PageHeader/Title';
import TabPanel from '../../components/TabPanel';
import DetailPageComponent from '../../components/DetailPageComponent';
import CitationFiles from './Tabs/Files';
import CitationDetail from './CitationDetail';
import CitationData from './Tabs/CitationData';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import { Form, MergeTemplate, Order, WorkflowTransition } from '../../store/modules/application/types';
import { Person } from '../../store/modules/person/types';
import { Citation, CitationTypeConfiguration } from '../../store/modules/citation/types';
import PageNotFound from '../../components/PageNotFound';
import Loader from '../../components/Loader';
import Fees from './Tabs/Fees';
import { CITATION_DETAILS_TAB_LIST, CITATION_STATUS } from '../../store/app.constants';
import { useAuthenticationContext } from '../../authentication';
import { siteActions } from '../../store/actions';
import { Api } from '../../store/services/services';
import CitationComment from './Tabs/CitationComment';
import CitationLicenses from './Tabs/CitationLicenses';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';

type CitationDetailTabsProps = {
    match: { params: { id: string } };
    getCitationDetails: Function;
    getPersonDetail: Function;
    isLoading: boolean;
    editForm: Form;
    citationDetail: Citation;
    setActiveTab: Function;
    getCitationFileList: Function;
    hasMoreFiles: boolean;
    citationType: CitationTypeConfiguration;
    updateCitation: Function;
    personDetail: Person;
    hasMoreCitationComments: boolean;
    getCitationCommentList: Function;
    getMergeTemplatesBegin: Function;
    mergeTemplatesList: MergeTemplate[];
    mergeDocuments: Function;
};

const CitationDetailTabs: FunctionComponent<CitationDetailTabsProps> = (props) => {
    const leftDetailBarStyle = classNames({
        'detail-container-closed': true,
        'detail-container-hidden': false,
    });

    const [doShowDetail, setDoShowDetail] = useState(true);
    const [citationLicensesSortOrder, setCitationLicensesSortOrder] = useState<Order>('desc');
    const [citationLicensesSortColumn, setCitationLicensesSortColumn] = useState<string>('expirationDate');
    const citationId = props.match.params.id;

    const [skipFile, setSkipFile] = useState(0);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const FileLimit = 20;

    const {
        getCitationDetails,
        getPersonDetail,
        isLoading,
        editForm,
        citationDetail,
        personDetail,
        citationType,
        setActiveTab,
        updateCitation,
        hasMoreCitationComments,
        getCitationCommentList,
        getCitationFileList,
        hasMoreFiles,
        getMergeTemplatesBegin,
        mergeTemplatesList,
        mergeDocuments,
    } = props;

    const personId = citationDetail?.person?.id;
    const authContext = useAuthenticationContext();
    const getAccessToken = authContext.getAccessToken;
    const scopes = authContext.scopes;
    const [skipRecord, setSkipRecord] = useState(0);
    const [statusChangeError, setStatusChangeError] = useState(false);
    const recordLimit = 20;
    const dispatch = useDispatch();

    const [statusList, setStatusList] = useState<string[]>();

    const getStatusList = useCallback(async () => {
        let statuses: string[] | undefined;

        if (citationType.workflowId) {
            const citationStatus = citationDetail?.status;
            try {
                const token = await getAccessToken({ scopes: [scopes['workflows.read']] });
                const transitions: WorkflowTransition[] | undefined = await Api.getWorkflowTransitions(
                    token.accessToken,
                    citationType.workflowId,
                    citationStatus,
                );
                statuses = transitions?.map((transition) => transition.nextState).concat(citationDetail.status);
            } catch (err) {
                dispatch(siteActions.setError(err));
            }
        }
        setStatusList(uniq((statuses ?? CITATION_STATUS).sort()));
    }, [dispatch, citationType.workflowId, citationDetail, getAccessToken, scopes]);

    useEffect(() => {
        getCitationDetails(citationId);
    }, [citationId, getCitationDetails]);

    const updateCitationLicensesSortBy = (sortBy: string) => {
        setCitationLicensesSortColumn(sortBy);
    };

    const updateCitationLicensesSortOrder = (sortOrder: Order) => {
        setCitationLicensesSortOrder(sortOrder);
    };
    useEffect(() => {
        getCitationCommentList(citationId, { limit: recordLimit, skip: skipRecord });
    }, [getCitationCommentList, citationId, skipRecord]);

    useEffect(() => {
        getCitationFileList(citationId, { limit: FileLimit, skip: skipFile });
    }, [getCitationFileList, citationId, skipFile]);

    const handleUploadFile = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setIsUploadModalOpen(true);
    };

    useEffect(() => {
        const subTypes = [citationDetail?.type?.name, undefined];
        const payload = {
            where: { type: 'Citation', subType: { inq: subTypes } },
            order: ['name ASC'],
            fields: {
                id: true,
                name: true,
                type: true,
            },
        };
        getMergeTemplatesBegin(payload);
    }, [getMergeTemplatesBegin, citationDetail]);

    useEffect(() => {
        personId && getPersonDetail(personId);
    }, [personId, getPersonDetail]);

    useEffect(() => {
        citationType.id && getStatusList();
    }, [citationType.id, getStatusList]);

    const handleStatusChange = (event: React.ChangeEvent<{}>, newStatus: string | null) => {
        if (newStatus && newStatus !== citationDetail.status) {
            if (newStatus === 'Processed' && !citationDetail?.monetaryPenalty?.fees?.length) {
                setStatusChangeError(true);
            } else {
                updateCitation({ status: newStatus }, citationId);
            }
        }
    };

    const classes = makeStyles(() => ({
        statusBox: {
            margin: '8px 20px',
            color: '#6E7788',
            fontSize: 16,
        },
        downloadBtn: {
            marginLeft: 5,
            fontSize: 12,
        },
    }))();

    const StatusHeader = () => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);

        const handleClickDownload = (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
        };
        const handleCloseDownload = (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setAnchorEl(null);
        };

        const handleSelect = (
            event: React.MouseEvent<HTMLElement>,
            selectedTemplateId: string,
            selectedTemplateName: string,
        ) => {
            event.stopPropagation();
            setAnchorEl(null);
            mergeDocuments(selectedTemplateId, selectedTemplateName, '', [citationId]);
        };

        return (
            <Header>
                <Snackbar
                    open={statusChangeError}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    autoHideDuration={3000}
                    onClose={() => setStatusChangeError(false)}
                >
                    <Alert severity="error" onClose={() => setStatusChangeError(false)}>
                        Citation cannot be processed when there are no associated fees
                    </Alert>
                </Snackbar>
                <Grid item xs={6} sm={6} md={6} lg={6}>
                    <Box>
                        <Title titleText={`${citationDetail.type.name} ${citationDetail.citationNumber || ''}`} />
                    </Box>
                </Grid>
                <Grid item xs={6} sm={6} md={6} lg={6}>
                    <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2} mb={2} ml={4} mr={4}>
                        <Box display="flex" className={classes.statusBox}>
                            Status
                        </Box>
                        {statusList ? (
                            <Autocomplete
                                id="citation-status-customized-select"
                                value={citationDetail.status}
                                onChange={handleStatusChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        value={citationDetail.status}
                                        inputProps={{ ...params.inputProps, 'aria-label': 'citation status' }}
                                    />
                                )}
                                renderOption={(option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                )}
                                options={statusList}
                                style={{ minWidth: 200 }}
                            />
                        ) : (
                            <Skeleton variant="text" width={100} height={40} />
                        )}
                        {citationDetail.status === 'Processed' && (
                            <Button
                                className={classes.downloadBtn}
                                variant="outlined"
                                color="inherit"
                                endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                onClick={handleClickDownload}
                            >
                                DOWNLOAD NOTICES
                            </Button>
                        )}
                        <Menu
                            open={open}
                            onClose={handleCloseDownload}
                            onClick={handleCloseDownload}
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                            }}
                            getContentAnchorEl={null}
                            elevation={1}
                        >
                            {mergeTemplatesList.map((item) => (
                                <MenuItem
                                    style={{ fontSize: 14 }}
                                    key={item.id}
                                    value={item.name}
                                    onClick={(e) => handleSelect(e, item.id, item.name)}
                                >
                                    {item.name}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Grid>
                <>
                    {doShowDetail && (
                        <KeyboardArrowLeft
                            className="detail-show-hide-btn opened"
                            onClick={() => {
                                setDoShowDetail(!doShowDetail);
                            }}
                        />
                    )}
                    {!doShowDetail && (
                        <KeyboardArrowRight
                            className="detail-show-hide-btn closed"
                            onClick={() => {
                                setDoShowDetail(!doShowDetail);
                            }}
                        />
                    )}
                </>
            </Header>
        );
    };

    const renderTabsPanel = (activeTab: number) => {
        const tab = CITATION_DETAILS_TAB_LIST[activeTab];

        switch (tab?.id) {
            case 0:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <CitationData citationDetails={citationDetail} editForm={editForm} />
                    </TabPanel>
                );

            case 1:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <CitationLicenses
                            citationDetails={citationDetail}
                            sortOrder={citationLicensesSortOrder}
                            sortBy={citationLicensesSortColumn}
                            setSortColumn={updateCitationLicensesSortBy}
                            setSortOrder={updateCitationLicensesSortOrder}
                        />
                    </TabPanel>
                );
            case 2:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <Fees citation={citationDetail} />
                    </TabPanel>
                );
            case 3:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <CitationFiles
                            citationId={citationId}
                            isUploadModalOpen={isUploadModalOpen}
                            getFileList={getCitationFileList}
                            onCloseUploadModal={(isUploadModalOpen: boolean) => {
                                setIsUploadModalOpen && setIsUploadModalOpen(isUploadModalOpen);
                            }}
                            handleUploadFile={handleUploadFile}
                            skip={skipFile}
                            setSkip={setSkipFile}
                            hasMore={hasMoreFiles}
                        />
                    </TabPanel>
                );
            case 4:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <CitationComment
                            citationDetails={citationDetail}
                            skip={skipRecord}
                            setSkip={setSkipRecord}
                            hasMore={hasMoreCitationComments}
                            doShowDetail={doShowDetail}
                        />
                    </TabPanel>
                );
            default:
                return <h1>Something went wrong. We are on it.</h1>;
        }
    };

    const onTabSelect = (tabName: string) => {
        setActiveTab(tabName);
    };

    if (isLoading) return <Loader />;
    else if (!isLoading && !citationDetail?.id) return <PageNotFound />;

    return (
        <div className="verticalFillContainer">
            <StatusHeader />
            <Box width="100%" display="flex" className="detail detail-mobile verticalFillContent">
                <CitationDetail
                    className={leftDetailBarStyle}
                    doShowDetail={doShowDetail}
                    citationData={citationDetail}
                    address={personDetail.address}
                />
                <DetailPageComponent
                    displayTabs={renderTabsPanel}
                    tabList={CITATION_DETAILS_TAB_LIST}
                    onTabSelect={onTabSelect}
                ></DetailPageComponent>
            </Box>
        </div>
    );
};

export default CitationDetailTabs;
